import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiPhone, FiMail, FiCheck, FiClock, FiSearch, FiCalendar, FiMapPin, FiVideo, FiEdit2, FiAward, FiTrendingUp } from 'react-icons/fi';
import AdminShell from '../../components/AdminShell/AdminShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import AdminWalletBadge from '../../components/AdminWalletBadge/AdminWalletBadge';
import Pagination from '../../components/Pagination/Pagination';
import panelStyles from '../../styles/adminPanels.module.css';
import styles from './AdminLeads.module.css';
import axiosClient from '../../api/axiosClient';
import { useCurrency } from '../../app/CurrencyContext';

interface CombinedLead {
  id: string;
  source_table: 'lead' | 'partner';
  lead_type: 'meeting' | 'invest' | 'partner';
  meeting_type: 'physical' | 'vc' | null;
  full_name: string;
  email: string;
  phone: string | null;
  fund_org: string | null;
  amount: number | null;
  currency: string | null;
  amount_inr: number | null;
  tier: string | null;
  message: string | null;
  status: string;
  contacted: boolean;
  contact_note: string | null;
  follow_up_date: string | null;
  created_at: string;
}

const TYPE_FILTERS = [
  { label: 'All types', value: 'all' },
  { label: 'Meetings', value: 'meeting' },
  { label: 'Investment', value: 'invest' },
  { label: 'Partner Program', value: 'partner' },
];

const PAGE_SIZE = 10;

const AdminLeads: React.FC = () => {
  const { formatTransaction } = useCurrency();
  const [leads, setLeads] = useState<CombinedLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [contactedFilter, setContactedFilter] = useState<'all' | 'true' | 'false'>('false');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [followUpDraft, setFollowUpDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    const params: Record<string, string> = {};
    if (contactedFilter !== 'all') params.contacted = contactedFilter;
    if (typeFilter !== 'all') params.leadType = typeFilter;
    if (search.trim()) params.search = search.trim();
    axiosClient.get('/admin/leads-and-applications', { params })
      .then(({ data }) => setLeads(data.leads))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(() => { load(); setPage(1); }, search ? 400 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactedFilter, typeFilter, search]);

  const toggleContacted = async (lead: CombinedLead) => {
    try {
      await axiosClient.patch(`/admin/contact-tracking/${lead.id}`, {
        source: lead.source_table,
        contacted: !lead.contacted,
      });
      toast.success(!lead.contacted ? 'Marked as contacted.' : 'Marked as not contacted.');
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update.');
    }
  };

  const startEdit = (lead: CombinedLead) => {
    setEditingId(lead.id);
    setNoteDraft(lead.contact_note || '');
    setFollowUpDraft(lead.follow_up_date ? lead.follow_up_date.slice(0, 10) : '');
  };

  const saveEdit = async (lead: CombinedLead) => {
    setSaving(true);
    try {
      await axiosClient.patch(`/admin/contact-tracking/${lead.id}`, {
        source: lead.source_table,
        note: noteDraft,
        followUpDate: followUpDraft || null,
      });
      toast.success('Saved.');
      setEditingId(null);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(leads.length / PAGE_SIZE));
  const pagedLeads = leads.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const describeLead = (lead: CombinedLead) => {
    if (lead.lead_type === 'meeting') {
      return <>{lead.meeting_type === 'vc' ? <FiVideo /> : <FiMapPin />} {lead.meeting_type === 'vc' ? 'Virtual meeting' : 'In-person meeting'}</>;
    }
    if (lead.lead_type === 'partner') {
      return <><FiAward /> {lead.tier} Partner application</>;
    }
    return <><FiTrendingUp /> Investment interest{lead.amount ? ` — ${formatTransaction(Number(lead.amount), lead.currency || 'INR', Number(lead.amount_inr ?? lead.amount))}` : ''}</>;
  };

  return (
    <AdminShell>
      <PageHeader
        title="Leads"
        subtitle="Everyone who submitted a form on the landing page — meetings, investment interest, and Partner Program applications. Track who's been contacted, no approval needed here."
        action={<AdminWalletBadge />}
      />
      <div className={panelStyles.page}>
        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <FiSearch className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              placeholder="Search by name, email or phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select className={styles.typeSelect} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            {TYPE_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          <div className={panelStyles.filters}>
            {[
              { label: 'Not contacted', value: 'false' },
              { label: 'Contacted', value: 'true' },
              { label: 'All', value: 'all' },
            ].map((f) => (
              <button
                key={f.value}
                className={`${panelStyles.filterBtn} ${contactedFilter === f.value ? panelStyles.filterBtnActive : ''}`}
                onClick={() => setContactedFilter(f.value as any)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading && <div className={panelStyles.loading}><span className={panelStyles.spinnerLg} /></div>}
        {!loading && leads.length === 0 && <div className={panelStyles.empty}>No leads match this filter.</div>}

        {!loading && pagedLeads.map((lead) => (
          <div key={`${lead.source_table}-${lead.id}`} className={styles.card}>
            <div className={styles.cardTop}>
              <div className={styles.cardMain}>
                <button
                  className={`${styles.contactToggle} ${lead.contacted ? styles.contactToggleOn : ''}`}
                  onClick={() => toggleContacted(lead)}
                  title={lead.contacted ? 'Mark as not contacted' : 'Mark as contacted'}
                >
                  {lead.contacted ? <FiCheck /> : <FiClock />}
                </button>
                <div>
                  <b className={styles.name}>{lead.full_name}</b>
                  <div className={styles.subline}>
                    {describeLead(lead)}
                    {' · '}{new Date(lead.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div className={styles.contactLinks}>
                {lead.phone && <a href={`tel:${lead.phone}`} className={styles.contactLink} title={lead.phone}><FiPhone /></a>}
                <a href={`mailto:${lead.email}`} className={styles.contactLink} title={lead.email}><FiMail /></a>
              </div>
            </div>

            {lead.follow_up_date && (
              <div className={styles.followUpChip}>
                <FiCalendar /> Follow up on {new Date(lead.follow_up_date).toLocaleDateString()}
              </div>
            )}
            {lead.contact_note && editingId !== lead.id && (
              <p className={styles.noteText}>{lead.contact_note}</p>
            )}

            {editingId === lead.id ? (
              <div className={styles.editBlock}>
                <textarea
                  className={styles.noteInput}
                  placeholder="What happened on the call? (e.g. interested, call back next week, not interested…)"
                  rows={2}
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                />
                <div className={styles.editRow}>
                  <input
                    type="date"
                    className={styles.dateInput}
                    value={followUpDraft}
                    onChange={(e) => setFollowUpDraft(e.target.value)}
                  />
                  <button className={panelStyles.actionBtn} onClick={() => saveEdit(lead)} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button className={panelStyles.actionBtnReject} onClick={() => setEditingId(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <button className={styles.editNoteBtn} onClick={() => startEdit(lead)}>
                <FiEdit2 /> {lead.contact_note || lead.follow_up_date ? 'Edit note / follow-up' : 'Add note / follow-up'}
              </button>
            )}
          </div>
        ))}

        {!loading && leads.length > PAGE_SIZE && (
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        )}
      </div>
    </AdminShell>
  );
};

export default AdminLeads;
